import { downloadURI } from '@utils/downloadDataURI';
import type { DragNDropFieldPropLocale } from 'uikit';
import { DragNDropField, Button, Attachment, Loader, IconClose, IconTrash, cnMixSpace, Tooltip } from 'uikit';

import React, { useEffect, useRef } from 'react';
import type { ControllerFieldState } from 'react-hook-form';

import { formatBytes, formatDateToLocale, getFileExtention } from '../../utils';

import Styles from './FileSelector.module.css';

export interface IFilesSelectorProps {
    accept?: string | string[];
    multiple?: boolean;
    onFilesChange: (files?: File[], replaceAll?: boolean) => void;
    isLoading?: boolean;
    typeName?: string;
    manualOperationName?: string;
    withoutEndingOfWordFile?: boolean;
    disabled?: boolean;
    hideSelected?: boolean;
    files?: File[];
    isExternalFilesState?: boolean;
    locale?: DragNDropFieldPropLocale;
    heightContent?: boolean;
    fullHeight?: boolean;
    customNameFile?: string;
    maxSize?: number;
    fieldState?: ControllerFieldState;
    isTooltipHidden?: boolean;
    withDownloadAction?: boolean;
    isTrashDeleteIcon?: boolean;
}

const formatPresentation = (v: string) => v.replace('.', '').toUpperCase();
const extensionRegExp = /\.(?!.*\.)(\w*)/;

export const FilesSelector: React.FC<IFilesSelectorProps> = (props) => {
    const {
        accept,
        maxSize,
        onFilesChange,
        multiple,
        isLoading,
        typeName,
        manualOperationName,
        withoutEndingOfWordFile,
        disabled,
        hideSelected,
        locale,
        isExternalFilesState,
        files: externalStateFiles,
        heightContent,
        fullHeight,
        customNameFile,
        fieldState,
        isTooltipHidden,
        withDownloadAction,
        isTrashDeleteIcon,
    } = props;

    const [files, setFiles] = React.useState<File[] | undefined>([]);

    const ref = useRef(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isExternalFilesState) {
            onFilesChange?.(files);
        }
    }, [files, isExternalFilesState, onFilesChange]);

    useEffect(() => {
        const emptyFiles: React.SetStateAction<File[]> = [];

        if (!isExternalFilesState) {
            setFiles(emptyFiles);
            onFilesChange?.(emptyFiles);
        }
    }, [accept, isExternalFilesState, onFilesChange]);

    return (
        <div
            className={`${Styles.filesSelector} ${heightContent ? 'heightContent' : ''} ${
                fullHeight ? 'fullHeight' : ''
            } wrapperDragNDrop`}
        >
            {!isLoading && (
                <>
                    <DragNDropField
                        accept={accept}
                        onDropFiles={isExternalFilesState ? onFilesChange : setFiles}
                        multiple={multiple}
                        disabled={disabled}
                        locale={locale}
                        maxSize={maxSize}
                        ref={ref}
                    >
                        {({ openFileDialog }) => (
                            <div className="selectorBody">
                                <div className="mainMessage">
                                    Перетащите {typeName || ''} файл{withoutEndingOfWordFile ? '' : '(ы)'}{' '}
                                    {customNameFile || ''} или{' '}
                                    <Button
                                        className="manualSelect"
                                        onClick={(e) => {
                                            e.preventDefault();

                                            return openFileDialog();
                                        }}
                                        label={manualOperationName || 'выберите вручную'}
                                        view="clear"
                                        disabled={disabled}
                                    />
                                </div>
                                {accept?.length && (
                                    <div className="formatsMessage">
                                        Поддерживаемые форматы:{' '}
                                        {Array.isArray(accept)
                                            ? accept.map((v) => formatPresentation(v)).join(', ')
                                            : formatPresentation(accept)}
                                    </div>
                                )}

                                {maxSize && (
                                    <div className="formatsMessage">
                                        Максимальный объём: {`${formatBytes(maxSize)}`}
                                    </div>
                                )}
                            </div>
                        )}
                    </DragNDropField>
                    {!isTooltipHidden && fieldState?.invalid && fieldState?.error?.message && (
                        <Tooltip
                            size="s"
                            direction="rightCenter"
                            possibleDirections={['rightCenter']}
                            status="alert"
                            anchorRef={ref}
                            className="tooltip-file-selector-name"
                            ref={tooltipRef}
                            style={{ zIndex: 9999 }}
                        >
                            {fieldState.error.message}
                        </Tooltip>
                    )}
                    {!disabled &&
                        !hideSelected &&
                        (isExternalFilesState ? externalStateFiles : files)?.map((file) => (
                            <Attachment
                                key={file.name}
                                fileName={file.name.replace(extensionRegExp, '')}
                                fileExtension={file.name.match(extensionRegExp)?.[1] || getFileExtention(file.type)}
                                fileDescription={`${formatBytes(file.size)} ${formatDateToLocale(
                                    new Date(file.lastModified),
                                    true,
                                )}`}
                                buttonIcon={isTrashDeleteIcon ? IconTrash : IconClose}
                                className={cnMixSpace({ mT: 'xs' })}
                                buttonTitle="Удалить"
                                onButtonClick={(e) => {
                                    e.stopPropagation();
                                    const fls = isExternalFilesState ? externalStateFiles : files;
                                    const newFiles = fls?.filter((f) => f !== file);

                                    if (isExternalFilesState) {
                                        onFilesChange(newFiles, multiple || undefined);
                                    } else {
                                        setFiles(newFiles);
                                    }
                                }}
                                onClick={
                                    withDownloadAction
                                        ? (e: any) => {
                                              e?.stopPropagation();
                                              const reader = new FileReader();

                                              reader.addEventListener(
                                                  'load',
                                                  () => downloadURI(reader.result as string, file.name),
                                                  false,
                                              );
                                              reader.readAsDataURL(file);
                                          }
                                        : undefined
                                }
                            />
                        ))}
                </>
            )}
            {isLoading && <Loader />}
        </div>
    );
};

FilesSelector.displayName = 'FilesSelector';
